import { NextFunction, Request, Response } from "express";

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

/**
 * Middleware to require specific roles
 * @param roles - Array of allowed roles
 */
export const requireRole = (roles: string[]) => {
  return (req: Request, res: Response, next: NextFunction): void => {
    const authReq = req as AuthRequest;
    const userRole = authReq.user?.role;

    if (!userRole) {
      res.status(401).json({
        success: false,
        message: "Authentication required",
        error: "No user role found",
      });
      return;
    }

    if (!roles.includes(userRole)) {
      res.status(403).json({
        success: false,
        message: "Access denied",
        error: `Required roles: ${roles.join(", ")}. Your role: ${userRole}`,
      });
      return;
    }

    next();
  };
};

/**
 * Middleware to require admin role
 */
export const requireAdmin = requireRole(["admin"]);

/**
 * Middleware to require school role
 */
export const requireSchool = requireRole(["school"]);

/**
 * Middleware to require teacher role
 */
export const requireTeacher = requireRole(["teacher"]);

/**
 * Middleware to require student role
 */
export const requireStudent = requireRole(["student"]);

/**
 * Middleware to require school or admin role
 */
export const requireSchoolOrAdmin = requireRole(["school", "admin"]);

/**
 * Middleware to require teacher or admin role
 */
export const requireTeacherOrAdmin = requireRole(["teacher", "admin"]);

/**
 * Middleware to require student or admin role
 */
export const requireStudentOrAdmin = requireRole(["student", "admin"]);
